// Iter 622 — Laporan Mingguan Pengiriman Shopee (data resmi Shopee; bukan status pekerjaan TAMFARIS).
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChevronRight, RefreshCw, X } from "lucide-react";
import { getWithRetry, describeLoadError } from "./resilient_get";
import { CaseBadge, OpenCases, ReturnCaseModal, useCaseSummaries } from "./shopee_return_case";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shopee-shipping-report`;
const TOKO_API = `${process.env.REACT_APP_BACKEND_URL}/api/marketplace/tiktok/shipping-report`;
const CH = { shopee: { api: API, name: "Shopee" }, tokopedia: { api: TOKO_API, name: "Tokopedia" } };
const KIND = { batal_otomatis: "Batal otomatis (tidak dikirim)", batal_penjual: "Dibatalkan penjual", batal_lain: "Pembatalan alasan lain", pengembalian: "Pengembalian" };
const TSTAT = { terverifikasi: "Waktu terverifikasi", perkiraan: "Waktu perkiraan (update_time Shopee)", belum_terverifikasi: "Waktu belum terverifikasi" };
const MAIN = [
  ["gabungan", "Total Pembatalan & Pengembalian"],
  ["batal_otomatis", "Batal Otomatis (Tidak Dikirim)"],
  ["batal_penjual", "Dibatalkan Penjual"],
  ["pengembalian_disetujui", "Pengembalian Disetujui"],
  ["terlambat_kirim", "Terlambat Kirim"],
];
const CATS = [
  ["batal_otomatis", "Batal Otomatis (lewat batas kirim)"],
  ["terlambat_kirim", "Terlambat Kirim"],
  ["pengembalian_waktu_belum_terverifikasi", "Pengembalian Disetujui — Waktu Belum Terverifikasi"],
  ["pengembalian_lain", "Pengembalian Lain (diajukan/diproses/ditolak/selesai)"],
  ["lewat_jam_tanggal_sama", "Lewat Jam di Tanggal yang Sama (tidak terlambat)"],
  ["belum_dikirim_lewat_batas", "Belum Dikirim — Lewat Batas"],
  ["belum_terverifikasi", "Data Belum Terverifikasi"],
  ["batal_waktu_belum_terverifikasi", "Pembatalan — Terdeteksi, Waktu Batal Belum Terverifikasi"],
  ["batal_lain", "Pembatalan Alasan Lain (pelanggan / belum dibayar / lainnya — tidak masuk total utama)"],
];
const DAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" }) + " WIB" : "—");
const mondayOf = (offset) => {
  const d = new Date(Date.now() + 7 * 3600e3);
  const dow = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dow - 7 * offset);
  return d.toISOString().slice(0, 10);
};

function Row({ r, cases, onCase }) {
  return <li className="ssr-row" data-testid={`ship-row-${r.order_sn}`}>
    <div className="ssr-row-head"><b>{r.order_sn}</b><span>{r.shop || "—"}</span></div>
    {(r.kind && KIND[r.kind]) || r.return_sn ? <div className="ssr-row-kind">{KIND[r.kind] || ""}{r.return_sn ? ` · retur ${r.return_sn}` : ""}</div> : null}
    <dl className="ssr-row-meta" data-testid={`ship-row-time-${r.order_sn}`}>
      <dt>Batas kirim</dt><dd>{fmt(r.ship_by_date_iso)}</dd>
      <dt>Kejadian</dt><dd>{r.event_at ? fmt(r.event_at) : <>Belum terverifikasi{r.reference_at ? ` (perkiraan ${fmt(r.reference_at)})` : ""}</>}{r.time_status ? <small> · {TSTAT[r.time_status] || r.time_status}</small> : null}</dd>
      <dt>Status</dt><dd>{r.status_raw || "—"}</dd>
      {r.cancel_reason && <><dt>Alasan</dt><dd>{r.cancel_by || "?"}: {r.cancel_reason}</dd></>}
      {r.packages.length > 0 && <><dt>Paket</dt><dd>{r.packages.map(p => `${p.package_number || "-"} ${p.shipping_carrier || ""} ${p.tracking_number ? `resi ${p.tracking_number}` : "(resi belum tersedia)"}`).join(" · ")}</dd></>}
    </dl>
    {r.return_sn && onCase && r.shop !== "Tokopedia (API)" && <CaseBadge sn={r.return_sn} s={cases?.[r.return_sn]} onOpen={() => onCase(r.return_sn)}/>}
  </li>;
}

const STEP = { orders: "Pesanan & keterlambatan", tracking: "Resi terlambat", returns: "Pengembalian" };
const STEP_ST = { pending: "menunggu", running: "sedang dibaca", success: "berhasil", failed: "gagal" };
function RefreshStatus({ name = "Shopee", job, latest, lastOk, onRetry }) {
  const j = job || latest;
  if (!j) return null;
  const st = job ? "running" : j.status;
  const head = { running: `Sedang memperbarui dari ${name}…`, success: "Berhasil diperbarui", partial: "Sebagian data gagal diperbarui", failed: "Gagal diperbarui" }[st] || st;
  const steps = Object.entries(j.steps || {});
  return <div className={`ssr-status ssr-status-${st}`} role="status" data-testid="ship-refresh-status">
    <b data-testid="ship-refresh-status-head">{head}</b>{!job && j.finished_at ? <small> · {fmt(j.finished_at)}</small> : null}
    {steps.length > 0 && <ul>{steps.map(([k, v]) => <li key={k} data-testid={`ship-step-${k}`}>{STEP[k] || k}: {STEP_ST[v.status] || v.status}{v.count ? ` (${v.count})` : ""}{v.error ? ` — ${v.error}` : ""}</li>)}</ul>}
    {j.error && <div>{j.error}</div>}
    {(st === "failed" || st === "partial") && <div>Data belum terbaru{lastOk ? ` — menampilkan data terakhir berhasil ${fmt(lastOk)}` : ""}.
      <button type="button" className="soft-btn small-btn" style={{ marginLeft: 8 }} onClick={onRetry} data-testid="ship-refresh-retry">Coba Lagi</button></div>}
  </div>;
}

export function ShopeeShippingReport({ onClose, initialChannel = "shopee" }) {
  const [ch, setCh] = useState(initialChannel === "tokopedia" ? "tokopedia" : "shopee");
  const { api: API, name: CHN } = CH[ch];
  const isToko = ch === "tokopedia";
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [job, setJob] = useState(null);       // pembacaan yang sedang berjalan
  const [latest, setLatest] = useState(null);  // hasil pembacaan terakhir minggu ini
  const [open, setOpen] = useState("");
  const [caseSn, setCaseSn] = useState("");
  const [caseKey, setCaseKey] = useState(0);
  const week = mondayOf(offset);
  const retSns = data ? [...new Set(Object.values(data.rows || {}).flat().map(r => r.return_sn).filter(Boolean))].sort() : [];
  const [cases, reloadCases] = useCaseSummaries(retSns);
  const pollRef = useRef(null);
  const load = useCallback(async () => {
    try { setData((await getWithRetry(API, { params: { week } })).data); setErr(""); }
    catch (e) { setErr(describeLoadError(e).text); }
  }, [week, API]);
  const poll = useCallback((startedAt = Date.now()) => {
    clearTimeout(pollRef.current);
    pollRef.current = setTimeout(async () => {
      try {
        const r = (await getWithRetry(`${API}/refresh-status`, { params: { week } })).data;
        setLatest(r.latest);
        if (r.running && Date.now() - startedAt < 13 * 60e3) { setJob(r.running); poll(startedAt); return; }
        setJob(null);
        if (r.running) setErr("Status pembacaan belum diketahui. Tekan Coba Lagi.");
        load();
      } catch (e) { if (Date.now() - startedAt < 13 * 60e3) poll(startedAt); else { setJob(null); setErr(describeLoadError(e).text); } }
    }, 3000);
  }, [week, load, API]);
  useEffect(() => {
    setData(null); setJob(null); setLatest(null); setErr("");
    load();
    getWithRetry(`${API}/refresh-status`, { params: { week } }).then(r => {
      setLatest(r.data.latest); if (r.data.running) { setJob(r.data.running); poll(); }
    }).catch(() => {});
    return () => clearTimeout(pollRef.current);
  }, [load, poll, week, API]);
  const busy = !!job;
  const apiOff = isToko && data && !data.api_ready;
  const refresh = async () => {
    if (busy) return;
    setErr("");
    try {
      const r = (await axios.post(`${API}/refresh`, null, { params: { week }, withCredentials: true, timeout: 20000 })).data;
      setJob(r.job); poll();
    } catch (e) { setErr(describeLoadError(e).text); }
  };
  const t = { ...(data?.totals || {}), gabungan: data?.combined_total ?? 0 };
  const retOff = data?.returns_status !== "terhubung";
  const retFailed = latest?.steps?.returns?.status === "failed" || data?.last_refresh?.shops?.some(x => x.returns_error);
  const tileVal = (k) => (k === "pengembalian_disetujui" && retOff ? (retFailed ? "Gagal Diperbarui" : "Belum Terhubung") : (t[k] ?? 0));
  const cert = (k) => data?.certainty?.[k];
  const listOf = (k) => k === "gabungan" ? ["batal_otomatis", "batal_penjual", "pengembalian_disetujui"].flatMap(x => data.rows[x] || []) : (data.rows[k] || []);
  const notFinal = data && (!data.complete || retOff || apiOff);
  const cats = isToko ? [["resi_terlambat", "Resi Terlambat (siap kirim melewati batas resmi)"], ...CATS.slice(2)] : CATS.slice(2);
  const List = ({ k }) => <ul className="ssr-list" data-testid={`ship-list-${k}`}>{listOf(k).length ? listOf(k).map((r, i) => <Row key={`${r.order_sn}-${r.return_sn || r.kind}-${i}`} r={r} cases={cases} onCase={setCaseSn}/>) : <li className="ssr-empty">Tidak ada pesanan.</li>}</ul>;
  return (
    <section className="pmc ssr" data-testid="ship-report-panel">
      <header className="ssr-top">
        <div className="ssr-title"><h3>Laporan Pengiriman {CHN}</h3><span>Data resmi {isToko ? "API TikTok Shop/Tokopedia" : "Shopee"} · Senin–Minggu WIB</span></div>
        <button type="button" onClick={onClose} className="ssr-close" data-testid="ship-report-close"><X size={16}/> Tutup</button>
      </header>
      <nav className="ssr-weeks" aria-label="Pilih marketplace" data-testid="ship-channel-switch">
        {Object.entries(CH).map(([k, v]) => <button key={k} type="button" className={ch === k ? "active" : ""} onClick={() => { if (!busy) setCh(k); }}
          data-testid={`ship-channel-${k}`}>{v.name}</button>)}
      </nav>
      <nav className="ssr-weeks" aria-label="Pilih minggu">
        {[0, 1, 2, 3].map(o => <button key={o} type="button" className={offset === o ? "active" : ""} onClick={() => setOffset(o)}
          data-testid={`ship-week-${o}`}>{o === 0 ? "Minggu Ini" : `${o} Minggu Lalu`}</button>)}
      </nav>
      <button type="button" className="ssr-refresh" onClick={refresh} disabled={busy || apiOff} data-testid="ship-refresh"><RefreshCw size={15} className={busy ? "spin" : ""}/> {busy ? `Membaca ${CHN}…` : `Perbarui dari ${CHN}`}</button>
      {apiOff && <div className="ssr-alert" data-testid="ship-api-not-ready">{data.api_note} Tombol Perbarui aktif setelah izin API siap.</div>}
      <RefreshStatus name={CHN} job={job} latest={latest} lastOk={data?.last_refresh?.at} onRetry={refresh}/>
      {err && <div className="ssr-alert" data-testid="ship-error">{err} <button type="button" className="soft-btn small-btn" onClick={job ? undefined : (data ? refresh : load)} disabled={busy} data-testid="ship-error-retry">Coba Lagi</button></div>}
      {data && <>
        <div className="ssr-week" data-testid="ship-week-label">{data.week_start} s/d {data.week_end}<span data-testid="ship-last-ok">{data.last_refresh ? `Terakhir berhasil diperbarui ${fmt(data.last_refresh.at)}` : `Belum pernah berhasil dibaca dari ${CHN} untuk minggu ini`}</span></div>
        {notFinal && <div className="ssr-notfinal" data-testid="ship-incomplete"><b>Belum Final</b>
          <span>{apiOff ? "Data resmi API belum tersedia" : !data.complete ? "Data belum lengkap" : "Pengembalian belum terhubung"}{!data.last_refresh && !apiOff ? ` — tekan Perbarui dari ${CHN}` : ""}. Sebagian waktu kejadian masih perkiraan.</span></div>}
        <div className="ssr-cards" data-testid="ship-totals">
          {MAIN.map(([k, l]) => { const v = tileVal(k); return <div key={k} className={`ssr-card${k === "gabungan" ? " main" : ""}${open === k ? " active" : ""}`}>
            <button type="button" onClick={() => setOpen(open === k ? "" : k)} aria-expanded={open === k} data-testid={`ship-total-${k}`}>
              <span className="ssr-card-label">{l}</span>
              <strong data-testid={`ship-total-${k}-value`}>{v}{typeof v === "number" && <small> pesanan</small>}</strong>
              {cert(k) && <span className="ssr-card-cert" data-testid={`ship-total-${k}-certainty`}>{cert(k).terverifikasi} waktu terverifikasi · {cert(k).perkiraan} waktu perkiraan</span>}
              <span className="ssr-card-more">{open === k ? "Tutup daftar ▲" : "Lihat pesanan ▼"}</span>
            </button>
            {open === k && <List k={k}/>}
          </div>; })}
        </div>
        <h4 className="ssr-h">Rincian Harian</h4>
        <div className="ssr-days-wrap">
          <table className="ssr-days" data-testid="ship-days">
            <thead><tr><th></th>{DAYS.map(d => <th key={d}>{d}</th>)}</tr></thead>
            <tbody>
              <tr><th>Total</th>{data.days.map(d => <td key={d.date} data-testid={`ship-day-combo-${d.date}`}>{d.gabungan}</td>)}</tr>
              <tr><th>Batal otomatis</th>{data.days.map(d => <td key={d.date} data-testid={`ship-day-cancel-${d.date}`}>{d.batal_otomatis}</td>)}</tr>
              <tr><th>Batal penjual</th>{data.days.map(d => <td key={d.date} data-testid={`ship-day-seller-${d.date}`}>{d.batal_penjual}</td>)}</tr>
              <tr><th>Retur disetujui</th>{data.days.map(d => <td key={d.date} data-testid={`ship-day-return-${d.date}`}>{d.pengembalian_disetujui}</td>)}</tr>
              <tr><th>Terlambat</th>{data.days.map(d => <td key={d.date} data-testid={`ship-day-late-${d.date}`}>{d.terlambat_kirim}</td>)}</tr>
            </tbody>
          </table>
        </div>
        <h4 className="ssr-h">Kategori Lain</h4>
        <div className="ssr-others">
          {!isToko && <OpenCases onOpen={setCaseSn} reloadKey={caseKey}/>}
          {cats.map(([k, l]) => <div key={k} className="ssr-other">
            <button type="button" onClick={() => setOpen(open === k ? "" : k)} aria-expanded={open === k} data-testid={`ship-other-${k}`}>
              <span>{l}</span><b>{t[k] ?? 0}</b><i>{open === k ? "▲" : "▼"}</i></button>
            {open === k && <List k={k}/>}
          </div>)}
        </div>
        <details className="ssr-info" data-testid="ship-info">
          <summary>Informasi Data</summary>
          {isToko && <p data-testid="ship-toko-source">Sumber: {data.rules?.source}</p>}
          {data.history_from && <p>Riwayat pesanan tersinkron sejak {fmt(data.history_from)}.</p>}
          <p data-testid="ship-source-status">Sumber data: Order API {data.last_refresh ? (data.last_refresh.shops?.every(x => x.list_complete) ? "terbaca lengkap" : "belum lengkap (batas halaman)") : "belum dibaca"} · Returns API {retOff ? "Belum Terhubung" : "terhubung"}{data.last_refresh?.tracking_truncated ? " · resi sebagian belum diambil" : ""}.</p>
          {data.last_refresh?.shops?.some(x => x.returns_error) && <p data-testid="ship-returns-error">Returns API Shopee belum dapat dibaca: {data.last_refresh.shops.map(x => x.returns_error).filter(Boolean).join("; ")} — angka pengembalian belum lengkap.</p>}
          {data.late_comparison && <p data-testid="ship-late-comparison">Terlambat dihitung per TANGGAL WIB (pickup setelah tanggal batas kirim). Sebelumnya (berbasis jam): <b>{data.late_comparison.sebelumnya_berbasis_jam}</b> · dikeluarkan karena tanggal sama: <b>{data.late_comparison.dikeluarkan_tanggal_sama}</b> · terlambat sebenarnya: <b>{data.late_comparison.terlambat_sebenarnya}</b> · belum terverifikasi: <b>{data.late_comparison.belum_terverifikasi}</b>. Bukan nilai resmi Kesehatan Toko Seller Centre.</p>}
          <p>Satuan: nomor pesanan unik. "Waktu perkiraan" memakai update_time Shopee bila waktu kejadian resmi belum tersedia.</p>
        </details>
      </>}
      {caseSn && <ReturnCaseModal sn={caseSn} onClose={() => setCaseSn("")} onChanged={() => { reloadCases(); setCaseKey(k => k + 1); }}/>}
    </section>
  );
}

// Tombol pembuka Laporan Pengiriman Shopee + angka minggu ini (data & rumus sama: GET laporan minggu berjalan, tanpa panggil Shopee).
export function ShippingReportTile({ onOpen, channel = "shopee" }) {
  const ch = CH[channel] || CH.shopee;
  const [t, setT] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    getWithRetry(ch.api, { params: { week: mondayOf(0) } }).then(r => { if (alive) setT(r.data?.totals || {}); }).catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [ch.api]);
  const sub = err ? "angka gagal dimuat" : !t ? "memuat…"
    : `Batal otomatis ${t.batal_otomatis ?? 0} · Terlambat ${t.terlambat_kirim ?? 0}`;
  return <button type="button" className="ssr-row-btn" onClick={onOpen} data-testid="ship-report-open"
    title={`Mingguan · batal otomatis & terlambat kirim (data resmi ${ch.name}) · pilih Shopee / Tokopedia di dalam laporan`}>
    <span className="ssr-row-btn-label">Laporan Pengiriman {ch.name}</span>
    <small data-testid="ship-report-week-counts">{err ? sub : t ? `Minggu ini: Batal otomatis ${t.batal_otomatis ?? 0} · Terlambat kirim ${t.terlambat_kirim ?? 0}` : sub}</small>
    <ChevronRight size={18} aria-hidden="true"/></button>;
}

export default ShopeeShippingReport;
